import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, routeMapping } from '../context/LanguageContext';
import { translations } from '../data/translations';
import SEOHead from '../components/SEOHead';

const WorksBilingual = () => {
  const { language, t } = useLanguage();
  const works = translations.works;
  const nav = translations.nav;
  
  const langPrefix = `/${language}`;
  const getRoute = (pageKey) => {
    const route = routeMapping[language][pageKey];
    return `${langPrefix}${route ? '/' + route : ''}`;
  };

  const caseStudies = [
    {
      id: 1,
      number: "01",
      title: works.case1Title,
      category: works.case1Category,
      image: "https://customer-assets.emergentagent.com/job_c17ab4df-56cb-4633-98aa-b5d05bd5d93e/artifacts/h3kcmjfy_image.png",
      challenge: works.case1Challenge,
      approach: works.case1Approach,
      outcome: works.case1Outcome
    },
    {
      id: 2,
      number: "02",
      title: works.case2Title,
      category: works.case2Category,
      image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_c17ab4df-56cb-4633-98aa-b5d05bd5d93e/artifacts/97853caa9c6a0361_gloria-villa-GjYw5xyMI3w-unsplash.webp",
      challenge: works.case2Challenge,
      approach: works.case2Approach,
      outcome: works.case2Outcome
    },
    {
      id: 3,
      number: "03",
      title: works.case3Title,
      category: works.case3Category,
      image: "https://customer-assets.emergentagent.com/job_c17ab4df-56cb-4633-98aa-b5d05bd5d93e/artifacts/p1i3lroe_image.png",
      challenge: works.case3Challenge,
      approach: works.case3Approach,
      outcome: works.case3Outcome
    },
    {
      id: 4,
      number: "04",
      title: works.case4Title,
      category: works.case4Category,
      image: "https://customer-assets.emergentagent.com/job_fenicis-hospitality/artifacts/lqcopfk7_luca-bravo-O453M2Liufs-unsplash.jpg",
      challenge: works.case4Challenge,
      approach: works.case4Approach,
      outcome: works.case4Outcome
    }
  ];

  const testimonials = [
    { quote: works.testimonial1, author: works.testimonial1Author },
    { quote: works.testimonial2, author: works.testimonial2Author },
    { quote: works.testimonial3, author: works.testimonial3Author }
  ];

  return (
    <div>
      <SEOHead 
        title={t(works.seoTitle)}
        description={t(works.seoDescription)}
      />
      
      {/* Hero Section */}
      <section className="section-padding-large" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
              {t(works.heroSubtitle)}
            </p>
            <h1 className="hero-large" style={{ marginBottom: '32px' }}>
              {t(works.heroTitle)}
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {t(works.heroDescription)}
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      {caseStudies.map((study, index) => (
        <section 
          key={study.id}
          className="section-padding" 
          style={{ background: index % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-primary)' }}
        >
          <div className="container">
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
              {/* Case Study Header */}
              <div style={{ marginBottom: '48px', textAlign: 'center' }}>
                <p className="meta-text" style={{ 
                  marginBottom: '16px',
                  color: 'var(--gold-accent)'
                }}>
                  {t(works.caseStudy)} {study.number}
                </p>
                
                <h2 className="heading-1" style={{ marginBottom: '12px' }}>
                  {t(study.title)}
                </h2>
                
                <p className="body-regular" style={{ 
                  color: 'var(--text-light)',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}>
                  {t(study.category)}
                </p>
              </div>
              
              {/* Case Study Image */}
              <div style={{ marginBottom: '48px', position: 'relative', height: '500px', overflow: 'hidden' }}>
                {study.fit === 'contain' && (
                  <img src={study.image} alt="" aria-hidden="true"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(100%) blur(24px)', transform: 'scale(1.15)', opacity: 0.6 }} />
                )}
                <img 
                  src={study.image}
                  alt={t(study.title)}
                  className="case-study-image"
                  data-testid={`case-study-image-${study.id}`}
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '500px',
                    objectFit: study.fit || 'cover',
                    objectPosition: 'center',
                    filter: 'grayscale(100%)'
                  }}
                />
              </div>
              
              {/* Case Study Content */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
                gap: '48px',
                maxWidth: '1000px',
                margin: '0 auto'
              }}>
                <div>
                  <h3 className="heading-3" style={{ 
                    marginBottom: '16px',
                    color: 'var(--gold-accent)'
                  }}>
                    {t(works.challenge)}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {t(study.challenge)}
                  </p>
                </div>
                
                <div>
                  <h3 className="heading-3" style={{ 
                    marginBottom: '16px',
                    color: 'var(--gold-accent)'
                  }}>
                    {t(works.approach)}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {t(study.approach)}
                  </p>
                </div>
                
                <div>
                  <h3 className="heading-3" style={{ 
                    marginBottom: '16px',
                    color: 'var(--gold-accent)'
                  }}>
                    {t(works.outcome)}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {t(study.outcome)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Discretion Note */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
              {t(works.discretionSubtitle)}
            </p>
            <h3 className="heading-2" style={{ marginBottom: '24px' }}>
              {t(works.discretionTitle)}
            </h3>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              {t(works.discretionDesc)}
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
            gap: '48px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {testimonials.map((testimonial, idx) => (
              <div key={idx} style={{ textAlign: 'center' }}>
                <p className="body-large" style={{ 
                  fontStyle: 'italic',
                  marginBottom: '16px',
                  color: 'var(--text-secondary)'
                }}>
                  "{t(testimonial.quote)}"
                </p>
                <p className="meta-text" style={{ color: 'var(--gold-accent)' }}>
                  — {t(testimonial.author)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>
              {t(works.ctaTitle)}
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '40px',
              color: 'var(--text-secondary)'
            }} dangerouslySetInnerHTML={{ __html: t(works.ctaDescription) }} />
            <Link to={getRoute('contact')} className="btn-primary" data-testid="cta-btn">
              {t(nav.cta)}
            </Link>
          </div>
        </div>
      </section>

      {/* Micro-SEO Block */}
      <section style={{ padding: '40px 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <p style={{ 
            fontSize: '12px',
            lineHeight: 1.7,
            color: 'var(--text-light)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {t(works.seoBlock)}
          </p>
        </div>
      </section>
    </div>
  );
};

export default WorksBilingual;
